/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { FormControlLabel, InputLabel, Radio, RadioGroup } from "@mui/material";
import { useStoreDispatch } from "../../../store/hooks";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import BasicDialog from "../../../components/Shared/Dialogs/BasicDialog";
import { radioNodeSelector } from "../../../store/api/devices/selectors";
import { IRadioNodesDataGridRow } from "../../../store/api/deviceConfig/types";
import { IBandData, IRadioNodeCellConfig } from "../../../store/api/devices/types";
import { useSelector } from "react-redux";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";
import { InputField } from "../../../components/Shared/Radix/Inputs/InputField";
import { Button } from "../../../components/ui/button";
import { Loader2 } from "lucide-react";

interface IAddEditRadioInfoProps {
  modelOpenMode?: string;
  onSubmitData?: () => void;
  setSelectedBandVal?: (val: string) => void;
  selectedRadioNode?: IRadioNodeCellConfig;
  modelKey?: string;
  cellType?: string;
  configStatus?: boolean;
  selectedBandVal?: string;
  radioType?: string;
  radioIndex?: string;
}

const AddEditRadioInfo: React.FC<IAddEditRadioInfoProps> = ({
  modelOpenMode = "Add",
  configStatus = false,
  selectedBandVal = "",
  onSubmitData,
  setSelectedBandVal,
  selectedRadioNode,
  modelKey = "",
  cellType = "",
  radioType = "Add",
  radioIndex,
}) => {
  const dispatch = useStoreDispatch();
  const radioNode: IRadioNodesDataGridRow = useSelector(radioNodeSelector);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const handleSubmit = () => {
    if (onSubmitData) {
      onSubmitData();
    }
  };

  const checkBandValidity = () => {
    let isValid = false;
    const bandsArray = cellType === "radio1" ? selectedRadioNode?.rnCellData?.radioBandOne || [] : selectedRadioNode?.rnCellData?.radioBandTwo || [];
    if (bandsArray.includes("B46 (LAA)")) {
      isValid = true;
    }
    return isValid;
  };

  const dialogAction = (
    <div className="flex items-center justify-around py-2">
      <Button variant="outline" onClick={() => dispatch(setBasicDialogOpenFor(undefined))}>Cancel</Button>
      {permissions?.hasOpCodeGroup("manage_topology") && (
        <Button
          disabled={configStatus || checkBandValidity()}
          onClick={handleSubmit}
        >
          {configStatus && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save
        </Button>
      )}
    </div>
  );

  const handleSelect = (event: any) => {
    if (setSelectedBandVal) {
      setSelectedBandVal(event.target.value);
    }
  };

  const handleIsCreateCell = () => {
    let isDisabled = false;
    if (cellType === "radio1") {
      if (Array.isArray(radioNode?.radios) && radioNode?.radios?.length > 0 && radioNode?.radios[0].index && radioNode?.radios[0].index === "1") {
        if (radioNode.radios[0].cellId) {
          isDisabled = true;
        }
      }
    } else {
      if (Array.isArray(radioNode?.radios) && radioNode?.radios?.length > 1 && radioNode?.radios?.length === 2 && radioNode.radios[1].index === "2") {
        if (radioNode.radios[1].cellId) {
          isDisabled = true;
        }
      }
    }
    return isDisabled;
  };

  const isBnadEnabled = (selectedBand: string, band: string) => {
    if (!handleIsCreateCell()) {
      return false;
    }
    if (selectedBand === band) {
      return false;
    } else return true;
  };

  const newFlatArray = (arr: string[]) => {
    return arr.flatMap((entry) => (entry?.includes("/") ? entry.split("/") : entry));
  };

  const showRadioBandData1 = () => {
    const AllBandData = selectedRadioNode?.bandData || [];
    const bandsArray = cellType === "radio1" ? selectedRadioNode?.rnCellData?.radioBandOne || [] : selectedRadioNode?.rnCellData?.radioBandTwo || [];
    const totalNoOfBands = newFlatArray(bandsArray);
    let radio1Bands: IBandData[] = [];
    radio1Bands = AllBandData?.filter((band) => band && totalNoOfBands.includes(band.band));
    if (cellType === "radio1") {
      if (radio1Bands?.length > 0) {
        return radio1Bands?.map((bandVal: IBandData) => {
          return (
            <FormControlLabel
              disabled={configStatus || isBnadEnabled(selectedBandVal, bandVal?.bandValue)}
              value={bandVal?.bandValue}
              control={<Radio />}
              label={bandVal?.band}
            />
          );
        });
      }
    } else {
      if (radio1Bands?.length > 0) {
        return radio1Bands?.map((bandVal: IBandData) => {
          return (
            <FormControlLabel
              disabled={configStatus || isBnadEnabled(selectedBandVal, bandVal?.bandValue)}
              value={bandVal?.bandValue}
              control={<Radio />}
              label={bandVal?.band}
            />
          );
        });
      }
    }
  };

  const showRadioBandData2 = () => {
    const AllBandData = selectedRadioNode?.bandData || [];
    const bandsArray = cellType === "radio1" ? selectedRadioNode?.rnCellData?.radioBandOne || [] : selectedRadioNode?.rnCellData?.radioBandTwo || [];
    const totalNoOfBands = newFlatArray(bandsArray);
    let newArrayBand: IBandData[] = [];
    if (bandsArray.includes("B46 (LAA)")) {
      return <FormControlLabel value={"B46"} control={<Radio />} label={"B46 (LAA)"} />;
    }
    newArrayBand = AllBandData?.filter((band) => band && totalNoOfBands.includes(band.band));
    if (newArrayBand?.length > 0) {
      return newArrayBand?.map((bandVal: IBandData) => {
        return (
          <FormControlLabel
            disabled={configStatus || isBnadEnabled(selectedBandVal, bandVal?.bandValue)}
            value={bandVal?.bandValue}
            control={<Radio />}
            label={bandVal?.band}
          />
        );
      });
    } else return;
  };

  return (
    <BasicDialog headerTitle={`${radioType} Radio Node`} dialogActions={dialogAction}>
      <form autoComplete="off">
        <div className="flex flex-col p-4 space-y-4">
          <div className="w-full">
            <InputField label="RadioNodeIndex" name="RadioNodeIndex" required disabled value={radioNode?.id || ""} onChange={() => {}} />
          </div>
          <div className="w-full">
            <InputField label="RadioIndex" name="radioIndex" disabled value={radioIndex || ""} onChange={() => {}} />
          </div>
          <div className="w-full">
            <InputLabel sx={{ minWidth: "205px" }}>{"Band"}</InputLabel>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              value={selectedBandVal || ""}
              name="row-radio-buttons-group"
              onChange={handleSelect}
            >
              {modelKey?.toLowerCase() === "scrn-310-04l2" ? showRadioBandData1() : showRadioBandData2()}
            </RadioGroup>
          </div>
        </div>
      </form>
    </BasicDialog>
  );
};

export default AddEditRadioInfo;
